import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useRunImpersonationScreeningMutation } from '@/store/api/Impersonation/Impersonation.query'
import { selectImpersonationCreditsFromStore } from '@/store/api/Users/Users.selector'

import { MockImpersonatorList } from './components/MockImpersonatorList'

export const ImpersonationScreening = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasRun = useRef(false)

  const impersonationCredits = useSelector(selectImpersonationCreditsFromStore)

  const [runScreening, { isLoading }] = useRunImpersonationScreeningMutation()
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    runScreening()
      .unwrap()
      .then((response) => {
        setCount(response.info.count)
      })
      .catch((error) => {
        catchWithSentry(error)
        toast.error(t('impersonation.screening.errorScreening'))
        navigate(AppRoutes.dashboard)
      })
  }, [runScreening, navigate, t])

  const onUnlock = () => {
    if (impersonationCredits > 0) {
      navigate(AppRoutes.impersonationGeneratingReport)
    } else {
      navigate(AppRoutes.impersonationPaywall)
    }
  }

  return (
    <WeScreen
      useNextButton={false}
      isLoading={isLoading || count === null}
      useHeaderBackButton
      onClickBack={() => navigate(AppRoutes.dashboard)}
    >
      <div className="flex w-full h-full items-center justify-center">
        <div className="flex flex-col p-5 gap-6 w-1/2 max-md:w-full max-lg:w-3/4 bg-baseColorHighlight rounded-2xl">
          <div className="flex flex-col gap-3 items-center text-center">
            <p className="text-3xl max-md:text-2xl font-medium text-neutralColor">
              {count === 0
                ? t('impersonation.screening.titleClean')
                : t('impersonation.screening.title')}
            </p>
            {count !== null && count > 0 && (
              <p className="text-xl max-md:text-lg font-normal text-neutralColor80">
                <Trans
                  i18nKey="impersonation.screening.subtitle"
                  values={{ total: count }}
                  components={{
                    bold: <span className="font-bold text-neutralColor" />,
                  }}
                />
              </p>
            )}
            {count === 0 && (
              <p className="text-xl max-md:text-lg font-normal text-neutralColor80">
                {t('impersonation.screening.subtitleClean')}
              </p>
            )}
          </div>

          {count !== null && count > 0 && (
            <>
              <MockImpersonatorList count={count} />

              <div className="flex flex-col gap-2 items-center">
                <Button
                  onClick={onUnlock}
                  className="font-medium text-base h-[46px] px-8 rounded-3xl py-3"
                >
                  {impersonationCredits > 0
                    ? t('impersonation.screening.runCta')
                    : t('impersonation.screening.unlockCta')}
                </Button>
                <p className="text-sm text-neutralColor60 text-center">
                  {t('impersonation.screening.unlockHint')}
                </p>
              </div>
            </>
          )}

          {count === 0 && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => navigate(AppRoutes.dashboard)}
                className="font-medium text-base h-[46px] px-8 rounded-3xl py-3"
              >
                {t('impersonation.screening.backToDashboard')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </WeScreen>
  )
}
